import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  downloadFileDto,
  GetCustomerFileListDto,
} from 'src/app/data/dtos/access-file/access-file.dto';
import { RefreshTokenDto } from 'src/app/data/dtos/auth/auth.dto';
import {
  GetDistrictRequestDto,
  GetDistrictResponseDto,
  GetHousingTypeResponseDto,
  GetOccupationGroupResponseDto,
  GetOccupationRequestDto,
  GetOccupationResponseDto,
  GetPostCodeRequestDto,
  GetPostCodeResponseDto,
  GetProvinceResponseDto,
  GetSubDistrictRequestDto,
  GetSubDistrictResponseDto,
} from 'src/app/data/dtos/data-dict/data-dict.dto';
import {
  NdidCheckRequestResponseDto,
  NdidRequestDataResponseDto,
} from 'src/app/data/dtos/ndid/ndid.dto';
import {
  GetAddressResponseDto,
  UpdateAddressRequestDto,
  updateTypeAddressRequestDto,
} from 'src/app/data/dtos/onboarding/address.dto';
import { VerifyIdCardNumberRequestDto } from 'src/app/data/dtos/onboarding/bulk.dto';
import { confirmDataRequestDto, confirmDataResponseDto } from 'src/app/data/dtos/onboarding/confirm-data.dto';
import {
  ConfirmOTPRequestDto,
  ContactRequestDto,
  SendOTPRequestDto,
} from 'src/app/data/dtos/onboarding/contact.dto';
import {
  DeleteDocumentRequestDto,
  UploadDocumentRequestDto,
  UploadOcrDocumentRequestDto,
  UploadSelfieRequestDto,
} from 'src/app/data/dtos/onboarding/document-source.dto';
import { GetPersonalLoanResponseDto } from 'src/app/data/dtos/onboarding/personal-loan.dto';
import {
  confirmPersonalLoanRequestDto,
  confirmProductRequestDto,
  getProductlistRequestDto,
  getProductlistResponseDto,
  GetProductResponseDto,
  setLoanTypeRequestDto,
  setProductRequestDto,
} from 'src/app/data/dtos/onboarding/product.dto';
import { getResumeStateResponseDto } from 'src/app/data/dtos/onboarding/resume.dto';
import {
  StartKYCRequestDto,
  StartKYCResponseDto,
} from 'src/app/data/dtos/onboarding/start-kyc.dto';
import {
  getCurrentStateResponseDto,
  UpdateCurrentStepRequestDto,
} from 'src/app/data/dtos/onboarding/state.dto';
import {
  AcceptTermAndConsentRequestDto,
  AcceptTermAndConsentResponseDto,
  GetTermAndConsentRequestDto,
  GetTermAndConsentResponseDto,
} from 'src/app/data/dtos/onboarding/term-consent.dto';
import {
  BeginTransactionRequestDto,
  BeginTransactionResponseDto,
  CloseKycResponseDto,
  CompareUserDataResponseDto,
  GetDocumentResponseDto,
  GetSelfieResponseDto,
  OnBoardingTransactionRequestDto,
  GetProductRequestDto,
  OptionalOnBoardingTransactionRequestDto,
  UpdateDocumentRequestDto,
  CheckLimitPolicyResponse,
  GetTxnResponseDto,
} from 'src/app/data/dtos/onboarding/transaction.dto';
import { LoanInfoResponseDto } from 'src/app/data/dtos/onboarding/user-info.dto';
import { UpdateWorkingRequestDto } from 'src/app/data/dtos/onboarding/working.dto';
import { ResponseDto } from 'src/app/data/dtos/response.dto';
import { StarMoneyAdapterService } from './star-money-adapter.service';
import { GetLoanTypeListResponseDto } from 'src/app/data/dtos/onboarding/loan-type.dto';
import { PersonalInfoUpdateRequestDto, PersonalInfoUpdateResponseDto } from 'src/app/data/dtos/onboarding/personal-info.dto';
import { IdempotencyService } from 'src/app/services/idempotency.service';

@Injectable({
  providedIn: 'root',
})
export class StarMoneyFacadeService {
  constructor(
    // private store: Store<StarMoneyState>,
    private starMoneyAdapterService: StarMoneyAdapterService,
    private idempotencyService: IdempotencyService,
    private http: HttpClient
  ) { }

  public async signInAnonymous(
    request: OptionalOnBoardingTransactionRequestDto
  ): Promise<ResponseDto<any>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.signInAnonymous(request)
    );
    return response;
  }

  public async refreshToken(
    request: RefreshTokenDto
  ): Promise<ResponseDto<any>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.refreshToken(request)
    );
    return response;
  }

  public async startKyc(
    request: StartKYCRequestDto
  ): Promise<ResponseDto<StartKYCResponseDto>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.startKyc(request)
    );
    return response;
  }

  async getWorkFlow(): Promise<ResponseDto<{ steps: string }>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.getWorkFlow()
    );
    return response;
  }

  async getCurrentWorkflow(): Promise<
    ResponseDto<{
      current_page_id: number;
      is_expired: boolean;
      status: string;
    }>
  > {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.getCurrentWorkFlow()
    );
    return response;
  }

  public async getCurrentState(
    request: OnBoardingTransactionRequestDto
  ): Promise<ResponseDto<getCurrentStateResponseDto>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.getCurrentState(request)
    );
    return response;
  }

  public async getTermAndConsent(
    request: GetTermAndConsentRequestDto
  ): Promise<ResponseDto<GetTermAndConsentResponseDto>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.getTermAndConsent(request)
    );
    return response;
  }

  public async acceptTermAndConsent(
    request: AcceptTermAndConsentRequestDto
  ): Promise<ResponseDto<boolean | AcceptTermAndConsentResponseDto>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.acceptTermAndConsent(request)
    );
    return response;
  }

  public async uploadOcrDocument(
    request: UploadOcrDocumentRequestDto
  ): Promise<ResponseDto<boolean>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.uploadOcrDocument(request)
    );
    return response;
  }

  public async processDoc(
    request: OnBoardingTransactionRequestDto
  ): Promise<ResponseDto<boolean>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.processDoc(request)
    );
    return response;
  }

  public async compareUserData(
    request: OnBoardingTransactionRequestDto
  ): Promise<ResponseDto<boolean | CompareUserDataResponseDto>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.compareUserData(request)
    );
    return response;
  }

  public async updateDocument(
    request: UpdateDocumentRequestDto
  ): Promise<ResponseDto<boolean>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.updateDocument(request)
    );
    return response;
  }

  public async updateContact(request: ContactRequestDto): Promise<any> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.updateContact(request)
    );
    return response;
  }

  public async getContact(
    request: OnBoardingTransactionRequestDto
  ): Promise<any> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.getContact(request)
    );
    return response;
  }

  public async sendOTP(request: SendOTPRequestDto): Promise<any> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.sendOTP(request)
    );
    return response;
  }

  public async confirmOTP(request: ConfirmOTPRequestDto): Promise<any> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.confirmOTP(request)
    );
    return response;
  }

  public async getAddress(
    request: OnBoardingTransactionRequestDto
  ): Promise<ResponseDto<GetAddressResponseDto[]>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.getAddress(request)
    );
    return response;
  }

  public async getWorking(
    request: OnBoardingTransactionRequestDto
  ): Promise<any> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.getWorking(request)
    );
    return response;
  }

  public async updateWorking(request: UpdateWorkingRequestDto): Promise<any> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.updateWorking(request)
    );
    return response;
  }

  public async updateAddress(request: UpdateAddressRequestDto): Promise<any> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.updateAddress(request)
    );
    return response;
  }

  public async updateTypeAddress(
    request: updateTypeAddressRequestDto
  ): Promise<any> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.updateTypeAddress(request)
    );
    return response;
  }

  public async uploadDocument(request: UploadDocumentRequestDto): Promise<any> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.uploadDocument(request)
    );
    return response;
  }

  public async getUploadFile(
    request: OnBoardingTransactionRequestDto
  ): Promise<any> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.getUploadFile(request)
    );
    return response;
  }

  public async getDocument(
    request: OnBoardingTransactionRequestDto
  ): Promise<ResponseDto<GetDocumentResponseDto>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.getDocument(request)
    );
    return response;
  }

  public async verifyDocument(
    request: OnBoardingTransactionRequestDto
  ): Promise<ResponseDto<boolean>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.verifyDocument(request)
    );
    return response;
  }

  public async uploadSelfie(
    request: UploadSelfieRequestDto
  ): Promise<ResponseDto<boolean>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.uploadSelfie(request)
    );
    return response;
  }

  public async getSelfie(
    request: OnBoardingTransactionRequestDto
  ): Promise<ResponseDto<GetSelfieResponseDto>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.getSelfie(request)
    );
    return response;
  }

  public async updateProcessing(
    request: OnBoardingTransactionRequestDto
  ): Promise<ResponseDto<boolean>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.updateProcessing(request)
    );
    return response;
  }

  public async getInfo(
    request: OnBoardingTransactionRequestDto
  ): Promise<ResponseDto<LoanInfoResponseDto>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.getInfo(request)
    );
    return response;
  }

  public async deleteFile(
    request: DeleteDocumentRequestDto
  ): Promise<ResponseDto<any>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.deleteFile(request)
    );
    return response;
  }

  public async getProductList(
    request: getProductlistRequestDto
  ): Promise<ResponseDto<getProductlistResponseDto[]>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.getProductList(request)
    );
    return response;
  }

  public async getProduct(
    request: GetProductRequestDto
  ): Promise<ResponseDto<GetProductResponseDto>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.getProduct(request)
    );
    return response;
  }

  public async confirmProduct(
    request: confirmProductRequestDto
  ): Promise<ResponseDto<any>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.confirmProduct(request)
    );
    return response;
  }

  public async setProduct(
    request: setProductRequestDto
  ): Promise<ResponseDto<boolean>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.setProduct(request)
    );
    return response;
  }

  public async getPersonalLoan(
    request: any
  ): Promise<ResponseDto<GetPersonalLoanResponseDto>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.getPersonalLoan(request)
    );
    return response;
  }

  public async getNanoFinance(
    request: any
  ): Promise<ResponseDto<GetPersonalLoanResponseDto>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.getNanoFinance(request)
    );
    return response;
  }

  public async confirmPersonalLoan(
    request: confirmPersonalLoanRequestDto
  ): Promise<ResponseDto<boolean>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.confirmPersonalLoan(request)
    );
    return response;
  }

  public async confirmNanoFinance(
    request: confirmPersonalLoanRequestDto
  ): Promise<ResponseDto<boolean>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.confirmNanoFinance(request)
    );
    return response;
  }

  public async updateCurrentStep(
    request: UpdateCurrentStepRequestDto
  ): Promise<ResponseDto<boolean>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.updateCurrentStep(request)
    );
    return response;
  }

  public async closeKyc(
    request: OnBoardingTransactionRequestDto
  ): Promise<ResponseDto<CloseKycResponseDto | boolean>> {
    const operation = `close-kyc:${request.txn_id}`;
    const idempotencyKey = this.idempotencyService.getOrCreate(operation);
    try {
      return await this.idempotencyService.run(operation, async () => {
        const response = await firstValueFrom(
          this.starMoneyAdapterService.closeKyc(request, idempotencyKey)
        );
        return response;
      });
    } catch (e) {
      this.idempotencyService.clear(operation);
      throw e;
    }

  }

  // ==========================================================
  // NDID
  // ==========================================================

  public async ndidRequestData(
    request: OnBoardingTransactionRequestDto
  ): Promise<ResponseDto<NdidRequestDataResponseDto>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.ndidRequestData(request)
    );
    return response;
  }

  public async ndidGetConsent(
    request: OnBoardingTransactionRequestDto
  ): Promise<ResponseDto<string>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.ndidGetConsent(request)
    );
    return response;
  }

  public async ndidCheckRequest(
    request: OnBoardingTransactionRequestDto
  ): Promise<ResponseDto<NdidCheckRequestResponseDto>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.ndidCheckRequest(request)
    );
    return response;
  }

  public async ndidCheckStatus(
    request: OnBoardingTransactionRequestDto
  ): Promise<ResponseDto<NdidCheckRequestResponseDto>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.ndidCheckStatus(request)
    );
    return response;
  }

  public async ndidCancelVerify(
    request: OnBoardingTransactionRequestDto
  ): Promise<ResponseDto<any>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.ndidCancelVerify(request)
    );
    return response;
  }

  //data dict
  public async getLoanTypeList(
    request: OnBoardingTransactionRequestDto
  ): Promise<ResponseDto<GetLoanTypeListResponseDto>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.getLoanTypeList(request)
    );
    return response;
  }

  public async setLoanType(request: setLoanTypeRequestDto): Promise<any> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.setLoanType(request)
    );
    return response;
  }

  public async getLoanType(
    request: OnBoardingTransactionRequestDto
  ): Promise<any> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.getLoanType(request)
    );
    return response;
  }

  public async getProvince(
    request: OnBoardingTransactionRequestDto
  ): Promise<ResponseDto<GetProvinceResponseDto[]>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.getProvince(request)
    );
    return response;
  }

  public async getDistrict(
    request: GetDistrictRequestDto
  ): Promise<ResponseDto<GetDistrictResponseDto[]>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.getDistrict(request)
    );
    return response;
  }
  public async getSubDistrict(
    request: GetSubDistrictRequestDto
  ): Promise<ResponseDto<GetSubDistrictResponseDto[]>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.getSubDistrict(request)
    );
    return response;
  }
  public async getPostCode(
    request: GetPostCodeRequestDto
  ): Promise<ResponseDto<GetPostCodeResponseDto[]>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.getPostCode(request)
    );
    return response;
  }
  public async getHousingType(
    request: OnBoardingTransactionRequestDto
  ): Promise<ResponseDto<GetHousingTypeResponseDto[]>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.getHousingType(request)
    );
    return response;
  }

  public async getOccupationGroup(
    request: OnBoardingTransactionRequestDto
  ): Promise<ResponseDto<GetOccupationGroupResponseDto[]>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.getOccupationGroup(request)
    );
    return response;
  }
  public async getOccupation(
    request: GetOccupationRequestDto
  ): Promise<ResponseDto<GetOccupationResponseDto[]>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.getOccupation(request)
    );
    return response;
  }

  public async resumeState(
    request: OnBoardingTransactionRequestDto & { type?: string }
  ): Promise<ResponseDto<getResumeStateResponseDto>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.resumeState(request)
    );
    return response;
  }

  public async confirmData(
    request: confirmDataRequestDto
  ): Promise<ResponseDto<confirmDataResponseDto>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.confirmData(request)
    );
    return response;
  }

  public async reject(
    request: OnBoardingTransactionRequestDto
  ): Promise<ResponseDto<any>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.reject(request)
    );
    return response;
  }

  public async customerAccessFile(
    request: GetCustomerFileListDto
  ): Promise<ResponseDto<any>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.customerAccessFile(request)
    );
    return response;
  }

  public async customerDownloadFile(request: downloadFileDto) {
    const response = this.starMoneyAdapterService.customerDownloadFile(request);
    return response;
  }

  public async saveCameraLog(request: any): Promise<ResponseDto<any>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.saveCameraLog(request)
    );
    return response;
  }

  public async beginTxn(
    request: BeginTransactionRequestDto
  ): Promise<ResponseDto<BeginTransactionResponseDto>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.beginTxn(request)
    );
    return response;
  }

  public async verifyIdCardNumber(
    request: VerifyIdCardNumberRequestDto
  ): Promise<ResponseDto<any>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.verifyIdCardNumber(request)
    );
    return response;
  }

  public async checkLimitPolicy(
    request: OnBoardingTransactionRequestDto
  ): Promise<ResponseDto<CheckLimitPolicyResponse>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.checkLimitPolicy(request)
    );
    return response;
  }

  public async getTxn(
    request: OnBoardingTransactionRequestDto,
  ): Promise<ResponseDto<GetTxnResponseDto>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.getTxn(request)
    );
    return response;
  }

  public async updatePersonalInfo(
    request: PersonalInfoUpdateRequestDto
  ): Promise<ResponseDto<PersonalInfoUpdateResponseDto>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.updatePersonalInfo(request)
    );
    return response;
  }

  public async getPersonalInfo(
    request: OnBoardingTransactionRequestDto,
  ): Promise<ResponseDto<PersonalInfoUpdateResponseDto>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.getPersonalInfo(request)
    );
    return response;
  }

  public async getAutoApprove(
    request: OnBoardingTransactionRequestDto,
  ): Promise<ResponseDto<any>> {
    const response = await firstValueFrom(
      this.starMoneyAdapterService.getAutoApprove(request)
    );
    return response;
  }
}
